import React, { useState, useEffect, useCallback, useRef } from "react";
import { Tab } from "@mui/base/Tab";
import { TabsList } from "@mui/base/TabsList";
import { TabPanel } from "@mui/base/TabPanel";
import { Tabs } from "@mui/base/Tabs";
import {
  getExpenses,
  postExpense,
  updateExpense,
  deleteExpense,
  getIncome,
  postIncome,
  updateIncome,
  deleteIncome,
  markIncomePaid,
} from "./services/api";
import { useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { useAccount } from "./hooks/useAccount";
import { useMonthlyEntries } from "./hooks/useMonthlyEntries";
import { useOutstandingIncome } from "./hooks/useOutstandingIncome";
import { EXPENSE_KIND, INCOME_KIND, ENTRY_KINDS } from "./constants/entryKinds";
import Login from "./components/Login";
import BottomSheet from "./components/BottomSheet";
import EntryForm from "./components/EntryForm";
import Home from "./pages/Home";
import EntriesPage from "./pages/EntriesPage";
import Settings from "./pages/Settings";
import { FaHome, FaArrowDown, FaDollarSign, FaCog } from "react-icons/fa";

const TAB_HOME = 1;
const TAB_INCOME = 2;
const TAB_EXPENSES = 3;
const TAB_SETTINGS = 4;

const KIND_OPTIONS = [
  { value: INCOME_KIND.key, label: "Income" },
  { value: EXPENSE_KIND.key, label: "Expense" },
];

// Inner component that uses theme context
function AppContent() {
  const { user, loading } = useAuth();
  const { theme } = useTheme();

  // Numbers, so they compare by value in the hooks' effect dependencies.
  const now = new Date();
  const thisMonth = now.getMonth() + 1;
  const thisYear = now.getFullYear();

  // Income and Expenses share one month cursor, so switching between them keeps
  // you on the month you were looking at.
  const [month, setMonth] = useState(thisMonth);
  const [year, setYear] = useState(thisYear);
  // Bumped by any mutation so every list re-syncs from the server.
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState(TAB_HOME);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Only consulted on Home, where the entry kind is genuinely ambiguous.
  const [homeKindKey, setHomeKindKey] = useState(EXPENSE_KIND.key);
  const [peekMessage, setPeekMessage] = useState("");
  const navRef = useRef(null);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  const account = useAccount();
  const expensesThisMonth = useMonthlyEntries(
    getExpenses, thisMonth, thisYear, refreshKey, EXPENSE_KIND.loadErrorText
  );
  const incomeThisMonth = useMonthlyEntries(
    getIncome, thisMonth, thisYear, refreshKey, INCOME_KIND.loadErrorText
  );
  const browsedExpenses = useMonthlyEntries(
    getExpenses, month, year, refreshKey, EXPENSE_KIND.loadErrorText
  );
  const browsedIncome = useMonthlyEntries(
    getIncome, month, year, refreshKey, INCOME_KIND.loadErrorText
  );
  const outstandingIncome = useOutstandingIncome(refreshKey);

  const dataError =
    account.error ||
    expensesThisMonth.error ||
    incomeThisMonth.error ||
    browsedExpenses.error ||
    browsedIncome.error ||
    outstandingIncome.error;

  // The sheet and the list padding both key off the real nav height, so measure
  // it rather than hardcoding a number that drifts when the nav is restyled.
  useEffect(() => {
    const node = navRef.current;
    if (!node) return undefined;
    const apply = () =>
      document.documentElement.style.setProperty(
        "--nav-height",
        `${node.offsetHeight}px`
      );
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, [user]);

  useEffect(() => {
    if (!peekMessage) return undefined;
    const id = setTimeout(() => setPeekMessage(""), 2000);
    // Cleared on unmount and on a rapid second add.
    return () => clearTimeout(id);
  }, [peekMessage]);

  // Selecting a kind from the drawer's tabs also opens it, so adding income
  // from Home is one tap. Switching while already open just switches —
  // setSheetOpen(true) is a no-op there, which is also what makes re-tapping
  // the already-selected tab do nothing.
  const handleKindSelect = useCallback((key) => {
    setHomeKindKey(key);
    setSheetOpen(true);
  }, []);

  const handleTabChange = (_event, value) => {
    setTab(value);
    // Collapse on tab change. Otherwise an expense form sits over the Income
    // list for a frame before the kind swaps, and any half-typed values would
    // silently retarget to a different table.
    setSheetOpen(false);
  };

  const gradient = theme.colors.gradient;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  const showSheet = tab !== TAB_SETTINGS;
  const sheetKind =
    tab === TAB_INCOME
      ? INCOME_KIND
      : tab === TAB_EXPENSES
      ? EXPENSE_KIND
      : ENTRY_KINDS[homeKindKey];
  const sheetCategories =
    sheetKind === INCOME_KIND ? account.incomeCategories : account.categories;
  const postEntry = sheetKind === INCOME_KIND ? postIncome : postExpense;

  const handleAdd = async (entry) => {
    // Deliberately not caught: EntryForm keeps the entered values and shows its
    // own error when this rejects, and the sheet stays open so the user can
    // retry. Everything below is the success path only.
    await postEntry(entry);

    setSheetOpen(false);
    setPeekMessage(sheetKind.successText);
    refresh();

    // A new entry is dated today. If the user is browsing an earlier month the
    // add would otherwise appear to do nothing — much more visible now that the
    // sheet sits directly on top of the list.
    if (month !== thisMonth || year !== thisYear) {
      setMonth(thisMonth);
      setYear(thisYear);
    }
  };

  // MUI Base's Tab sets aria-selected (not data-selected), so the selected
  // styling hangs off Tailwind's aria-* variants. The class strings themselves
  // live in ThemeContext as complete literals so Tailwind's scanner can find
  // them — building them here with interpolation would produce classes that
  // never make it into the stylesheet.
  const tabBaseClass = "flex flex-col items-center gap-1 px-2 py-3 rounded-2xl transition-all duration-200 hover:bg-gray-100 focus:outline-none group aria-selected:bg-gradient-to-r aria-selected:shadow-lg";
  const tabSelectedClass = gradient.navSelected;
  const tabIconClass = "text-lg text-gray-400 group-hover:text-gray-600 group-aria-selected:text-white transition-colors";
  const tabLabelClass = "text-xs font-medium text-gray-400 group-hover:text-gray-600 group-aria-selected:text-white transition-colors";

  return (
    <div className="bg-gray-50 min-h-screen">
      {dataError && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 text-sm px-4 py-3 flex items-center justify-between gap-3 relative z-50">
          <span>{dataError}</span>
          <button
            onClick={refresh}
            className="font-semibold underline underline-offset-2 shrink-0"
          >
            Retry
          </button>
        </div>
      )}
      <Tabs value={tab} onChange={handleTabChange}>
        <TabPanel value={TAB_HOME} className="pb-sheet-safe">
          <Home
            budget={account.budget}
            incomeGoal={account.incomeGoal}
            expensesThisMonth={expensesThisMonth.entries}
            incomeThisMonth={incomeThisMonth.entries}
            outstanding={outstandingIncome.entries}
          />
        </TabPanel>
        <TabPanel value={TAB_INCOME} className="pb-sheet-safe">
          <EntriesPage
            kind={INCOME_KIND}
            categories={account.incomeCategories}
            entries={browsedIncome.entries}
            setEntries={browsedIncome.setEntries}
            outstanding={outstandingIncome.entries}
            setOutstanding={outstandingIncome.setEntries}
            month={month}
            year={year}
            setMonth={setMonth}
            setYear={setYear}
            api={{ update: updateIncome, remove: deleteIncome, markPaid: markIncomePaid }}
            onMutate={refresh}
          />
        </TabPanel>
        <TabPanel value={TAB_EXPENSES} className="pb-sheet-safe">
          <EntriesPage
            kind={EXPENSE_KIND}
            categories={account.categories}
            entries={browsedExpenses.entries}
            setEntries={browsedExpenses.setEntries}
            month={month}
            year={year}
            setMonth={setMonth}
            setYear={setYear}
            api={{ update: updateExpense, remove: deleteExpense }}
            onMutate={refresh}
          />
        </TabPanel>
        <TabPanel value={TAB_SETTINGS} className="pb-nav-safe">
          <Settings account={account} />
        </TabPanel>

        {/* Before TabsList in DOM order so z-order and paint order agree. */}
        {showSheet && (
          <BottomSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            peekLabel={peekMessage || sheetKind.peekLabel}
            title={sheetKind.addLabel}
            // Home is the only tab where the kind is ambiguous, so it's the
            // only one that splits the handle. While a success message is
            // showing, the bar falls back to the single-label form to display
            // it — which is why peekMessage suppresses the tabs here.
            peekTabs={
              tab === TAB_HOME && !peekMessage
                ? {
                    options: KIND_OPTIONS,
                    value: homeKindKey,
                    onSelect: handleKindSelect,
                  }
                : null
            }
          >
            {/* Remounts when the kind changes, so a half-typed amount can't
                carry over into the other table. */}
            <EntryForm
              key={sheetKind.key}
              kind={sheetKind}
              categories={sheetCategories}
              onSubmit={handleAdd}
            />
          </BottomSheet>
        )}

        {/* Modern Bottom Navigation.
            z-50 is mandatory, not cosmetic: without it the nav paints by DOM
            order among positioned siblings and the sheet would cover it.

            bg-white/95 rather than /80: the collapsed sheet is translated down
            past the nav's top edge, so its lower half now sits permanently
            behind this bar. At 80% opacity the numpad ghosted through. */}
        <TabsList
          ref={navRef}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200 px-2 pt-2 pb-nav-inset grid grid-cols-4 gap-1 items-stretch shadow-lg"
        >
          <Tab value={TAB_HOME} className={`${tabBaseClass} ${tabSelectedClass}`}>
            <FaHome className={tabIconClass} />
            <span className={tabLabelClass}>Home</span>
          </Tab>
          <Tab value={TAB_INCOME} className={`${tabBaseClass} ${tabSelectedClass}`}>
            <FaArrowDown className={tabIconClass} />
            <span className={tabLabelClass}>Income</span>
          </Tab>
          <Tab value={TAB_EXPENSES} className={`${tabBaseClass} ${tabSelectedClass}`}>
            <FaDollarSign className={tabIconClass} />
            <span className={tabLabelClass}>Expenses</span>
          </Tab>
          <Tab value={TAB_SETTINGS} className={`${tabBaseClass} ${tabSelectedClass}`}>
            <FaCog className={tabIconClass} />
            <span className={tabLabelClass}>Settings</span>
          </Tab>
        </TabsList>
      </Tabs>
    </div>
  );
}

// Main App component with ThemeProvider wrapper
function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
